import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Loader2, 
  Search,
  MessageSquare,
  BarChart3,
  RefreshCw,
  ExternalLink,
  Newspaper,
  Globe
} from "lucide-react";
import { SiReddit } from "react-icons/si";
import { SEO } from "@/components/seo";
import { apiRequest } from "@/lib/queryClient";
import { MainLayout } from "@/components/layout/main-layout";

interface ProcessedPost {
  id: string;
  text: string;
  symbols: string[];
  normalizedScore: number;
  category: "positive" | "negative" | "neutral";
  author: string;
  score: number;
  source: string;
  url: string;
  publishedAt: string;
  userSentiment?: "Bullish" | "Bearish" | null;
}

interface SentimentFeed {
  posts: ProcessedPost[];
  aggregateSentiment: number;
  symbolMentions: Record<string, { count: number; avgSentiment: number }>;
  feedName: string;
  sources: { name: string; postCount: number }[];
}

function SentimentBadge({ score, userSentiment }: { score: number; userSentiment?: "Bullish" | "Bearish" | null }) {
  if (userSentiment === "Bullish" || score > 0.2) {
    return (
      <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
        <TrendingUp className="h-3 w-3 mr-1" />
        Bullish
      </Badge>
    );
  }
  if (userSentiment === "Bearish" || score < -0.2) {
    return (
      <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
        <TrendingDown className="h-3 w-3 mr-1" />
        Bearish
      </Badge>
    );
  }
  return (
    <Badge className="bg-gray-500/10 text-gray-600 border-gray-500/30">
      <Minus className="h-3 w-3 mr-1" />
      Neutral
    </Badge>
  );
}

function SentimentScore({ score }: { score: number }) {
  const color = score > 0.2 ? "text-green-500" : score < -0.2 ? "text-red-500" : "text-gray-500";
  return (
    <span className={`font-mono font-bold ${color}`} data-testid="text-sentiment-score">
      {score > 0 ? "+" : ""}{(score * 100).toFixed(0)}%
    </span>
  );
}

function SourceIcon({ source }: { source: string }) {
  if (source.startsWith("reddit")) {
    return <SiReddit className="h-3.5 w-3.5 text-orange-500" />;
  }
  if (source === "google_news") {
    return <Newspaper className="h-3.5 w-3.5 text-blue-500" />;
  }
  return <Globe className="h-3.5 w-3.5 text-muted-foreground" />;
}

function SourceLabel({ source }: { source: string }) {
  if (source.startsWith("reddit/r/")) {
    return <span className="text-orange-500">r/{source.replace("reddit/r/", "")}</span>;
  }
  if (source === "google_news") {
    return <span className="text-blue-500">Google News</span>;
  }
  return <span>{source}</span>;
}

export default function SentimentPage() {
  const [activeTab, setActiveTab] = useState("trending");
  const [symbolSearch, setSymbolSearch] = useState("");
  const [searchedSymbol, setSearchedSymbol] = useState("");
  const [postFilter, setPostFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState<string>("all");

  const { data: trendingData, isLoading: trendingLoading, refetch: refetchTrending, isFetching: trendingFetching } = useQuery<SentimentFeed>({
    queryKey: ["/api/sentiment/trending"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/sentiment/trending");
      return response.json();
    },
    refetchInterval: 120000,
    enabled: activeTab === "trending",
  });

  const { data: symbolData, isLoading: symbolLoading, refetch: refetchSymbol, isFetching: symbolFetching } = useQuery<SentimentFeed>({
    queryKey: ["/api/sentiment/symbol", searchedSymbol],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/sentiment/symbol/${searchedSymbol}`);
      return response.json();
    },
    refetchInterval: 120000,
    enabled: activeTab === "symbol" && !!searchedSymbol,
  });

  const currentData = activeTab === "trending" ? trendingData : symbolData;
  const isLoading = activeTab === "trending" ? trendingLoading : symbolLoading;
  const isFetching = activeTab === "trending" ? trendingFetching : symbolFetching;

  const handleRefresh = () => {
    if (activeTab === "trending") refetchTrending();
    else refetchSymbol();
  };

  const handleSymbolSearch = () => {
    const sym = symbolSearch.trim().toUpperCase().replace("$", "");
    if (sym) {
      setSearchedSymbol(sym);
      setActiveTab("symbol");
    }
  };

  const popularSymbols = ["AAPL", "TSLA", "NVDA", "SPY", "AMZN", "MSFT", "META", "AMD", "GME", "BTC"];

  const topSymbols = currentData?.symbolMentions
    ? Object.entries(currentData.symbolMentions)
        .sort(([, a], [, b]) => b.count - a.count)
        .slice(0, 10)
    : [];

  const filteredPosts = currentData?.posts.filter((post) => {
    if (sourceFilter !== "all") {
      const isReddit = post.source.startsWith("reddit");
      const isNews = post.source === "google_news";
      if (sourceFilter === "reddit" && !isReddit) return false;
      if (sourceFilter === "news" && !isNews) return false;
    }
    if (!postFilter) return true;
    const query = postFilter.toLowerCase();
    return (
      post.text.toLowerCase().includes(query) ||
      post.symbols.some((s) => s.toLowerCase().includes(query))
    );
  });

  return (
    <>
      <SEO
        title="Sentiment Analysis | TradePro"
        description="Real-time social media and news sentiment analysis for stock and crypto trading signals"
      />
      <MainLayout
        title="Sentiment Analysis"
        subtitle="AI-powered market sentiment from news and social media"
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
            data-testid="button-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      >
        <div className="space-y-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search symbol (e.g. AAPL, TSLA, BTC)..."
              value={symbolSearch}
              onChange={(e) => setSymbolSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSymbolSearch()}
              className="pl-9"
              data-testid="input-symbol-search"
            />
          </div>
          <Button onClick={handleSymbolSearch} data-testid="button-search-symbol">
            <Search className="h-4 w-4 mr-2" />
            Analyze
          </Button>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isFetching}
            data-testid="button-refresh"
            className="sm:hidden"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {popularSymbols.map((sym) => (
            <Button
              key={sym}
              variant="outline"
              size="sm"
              className={`toggle-elevate ${searchedSymbol === sym && activeTab === "symbol" ? "toggle-elevated" : ""}`}
              onClick={() => {
                setSearchedSymbol(sym);
                setSymbolSearch(sym);
                setActiveTab("symbol");
              }}
              data-testid={`button-symbol-${sym}`}
            >
              ${sym}
            </Button>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="trending" data-testid="tab-trending">
              <TrendingUp className="h-4 w-4 mr-2" />
              Trending
            </TabsTrigger>
            <TabsTrigger 
              value="symbol" 
              disabled={!searchedSymbol}
              data-testid="tab-symbol"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              {searchedSymbol ? `$${searchedSymbol}` : "Symbol"}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Fetching sentiment from Reddit and Google News...</p>
              </div>
            ) : !currentData || currentData.posts.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground text-lg">
                    {activeTab === "symbol" && !searchedSymbol
                      ? "Search for a stock symbol to see sentiment"
                      : "No posts found. Try a different symbol or check back later."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Overall Sentiment</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between gap-2">
                        <SentimentScore score={currentData.aggregateSentiment} />
                        <SentimentBadge score={currentData.aggregateSentiment} />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Posts Analyzed</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-muted-foreground" />
                        <span className="text-2xl font-bold" data-testid="text-post-count">
                          {currentData.posts.length}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Symbols Detected</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-muted-foreground" />
                        <span className="text-2xl font-bold" data-testid="text-symbol-count">
                          {topSymbols.length}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Data Sources</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-1">
                        {currentData.sources?.map((src) => (
                          <div key={src.name} className="flex items-center justify-between gap-2 text-sm">
                            <div className="flex items-center gap-1.5">
                              {src.name === "Reddit" ? (
                                <SiReddit className="h-3.5 w-3.5 text-orange-500" />
                              ) : (
                                <Newspaper className="h-3.5 w-3.5 text-blue-500" />
                              )}
                              <span>{src.name}</span>
                            </div>
                            <span className="text-muted-foreground">{src.postCount}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {topSymbols.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Top Mentioned Symbols</CardTitle>
                      <CardDescription>
                        Stock and crypto symbols with the most mentions and their average sentiment
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                        {topSymbols.map(([symbol, data]) => (
                          <button
                            key={symbol}
                            onClick={() => {
                              setSearchedSymbol(symbol);
                              setSymbolSearch(symbol);
                              setActiveTab("symbol");
                            }}
                            className="flex flex-col items-center p-3 bg-muted/50 rounded-md hover-elevate cursor-pointer"
                            data-testid={`symbol-card-${symbol}`}
                          >
                            <span className="font-bold text-lg">${symbol}</span>
                            <span className="text-sm text-muted-foreground">
                              {data.count} mention{data.count !== 1 ? "s" : ""}
                            </span>
                            <SentimentScore score={data.avgSentiment} />
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <CardTitle>Recent Posts</CardTitle>
                        <CardDescription>
                          Latest discussions from Reddit and Google News with sentiment analysis
                        </CardDescription>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className={`toggle-elevate ${sourceFilter === "all" ? "toggle-elevated" : ""}`}
                            onClick={() => setSourceFilter("all")}
                            data-testid="filter-all"
                          >
                            All
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className={`toggle-elevate ${sourceFilter === "reddit" ? "toggle-elevated" : ""}`}
                            onClick={() => setSourceFilter("reddit")}
                            data-testid="filter-reddit"
                          >
                            <SiReddit className="h-3.5 w-3.5 mr-1 text-orange-500" />
                            Reddit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className={`toggle-elevate ${sourceFilter === "news" ? "toggle-elevated" : ""}`}
                            onClick={() => setSourceFilter("news")}
                            data-testid="filter-news"
                          >
                            <Newspaper className="h-3.5 w-3.5 mr-1 text-blue-500" />
                            News
                          </Button>
                        </div>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Filter by symbol or text..."
                            value={postFilter}
                            onChange={(e) => setPostFilter(e.target.value)}
                            className="pl-9 w-full sm:w-48"
                            data-testid="input-filter"
                          />
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {filteredPosts?.slice(0, 30).map((post) => (
                        <div
                          key={post.id}
                          className="p-4 border rounded-md space-y-2"
                          data-testid={`post-card-${post.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <p className="text-sm line-clamp-3">{post.text}</p>
                            <SentimentBadge score={post.normalizedScore} userSentiment={post.userSentiment} />
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <SourceIcon source={post.source} />
                              <SourceLabel source={post.source} />
                            </div>
                            <span className="text-muted-foreground/40">|</span>
                            <span>{post.author}</span>
                            {post.score > 0 && (
                              <>
                                <span className="text-muted-foreground/40">|</span>
                                <span>{post.score.toLocaleString()} upvotes</span>
                              </>
                            )}
                            {post.symbols.length > 0 && (
                              <>
                                <span className="text-muted-foreground/40">|</span>
                                <div className="flex flex-wrap gap-1">
                                  {post.symbols.slice(0, 5).map((sym) => (
                                    <Badge key={sym} variant="outline" className="text-xs">
                                      ${sym}
                                    </Badge>
                                  ))}
                                </div>
                              </>
                            )}
                            <span className="text-muted-foreground/40">|</span>
                            <a
                              href={post.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 hover:text-foreground"
                              data-testid={`link-post-${post.id}`}
                            >
                              <ExternalLink className="h-3 w-3" />
                              View
                            </a>
                          </div>
                        </div>
                      ))}
                      {(!filteredPosts || filteredPosts.length === 0) && (
                        <div className="text-center py-8 text-muted-foreground">
                          No posts found matching your criteria
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
      </MainLayout>
    </>
  );
}
